import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { Collapsible } from "@/app/components/ui/collapsible";
import { FaqSection } from "@/app/components/seo-support";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { EN_ROUTE_FAQS } from "@/app/seo-content-en";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "@/app/pages/Docs/QuickStartShared";

const HOTLINE_ONBOARDING_BODY = `{
  "responder_id": "responder_xxx",
  "hotline_id": "your.namespace.tool-name.v1",
  "template_ref": "local.delegated-execution.workspace-summary.v1",
  "title": "Workspace summarizer",
  "summary": "Summarize a target workspace into a short agent-readable report.",
  "input_schema": {
    "type": "object",
    "required": ["text"],
    "properties": {
      "text": { "type": "string", "description": "user prompt" }
    }
  },
  "output_schema": {
    "type": "object",
    "required": ["summary"],
    "properties": {
      "summary": { "type": "string", "description": "workspace summary" }
    }
  }
}`;

const SAMPLE_REVIEW_ERROR = `{
  "status": "FAILED",
  "error": {
    "code": "SCHEMA_VALIDATION_FAILED",
    "message": "output_schema is missing required property definitions"
  }
}`;

export default function EnglishQuickStartResponder() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <QuickStartHero
        role="Responder · 5 min · EN"
        roleColor="#3B82F6"
        titleTop="Turn one"
        titleBottom="capability into a Hotline"
        lead="Start from the published delexec-ops package, prove the local runtime with the example Hotline, then attach your own capability to the supervisor. Source installs and review are later steps."
        meta={["Local first", "Platform optional", "Process or HTTP adapters"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">What this page gets you to</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">From zero to a locally callable Hotline draft ready for review</h2>
          <ul className="mt-4 grid gap-2 text-sm font-bold text-zinc-800 sm:grid-cols-2">
            <li>· Start supervisor and isolate local runtime state</li>
            <li>· Register and enable the Responder role</li>
            <li>· Validate the built-in example Hotline first</li>
            <li>· Create your own process or HTTP Hotline draft</li>
            <li>· Generate registration draft · optionally submit review · check local status</li>
          </ul>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="Official path: install the CLI and bootstrap"
            intent="New users should start from the published @delexec/ops package and confirm the local runtime, Caller/Responder loop, and example Hotline work before editing source."
          >
            <CodeBlock language="bash" caption="Any directory">{`npm install -g @delexec/ops
delexec-ops bootstrap --email you@example.com`}</CodeBlock>

            <Note label="Expected">
              These two commands install the CLI and initialize the local runtime. It is not a publishing step,
              but it confirms the runtime foundation for Responder-side work.
            </Note>

            <Collapsible title="Source install / Contributor path">
              <div className="space-y-2 text-sm text-zinc-600">
                <p>If you need to modify the CLI source or contribute:</p>
                <pre className="bg-zinc-100 p-3 rounded text-xs overflow-x-auto">
{`git clone --recursive https://github.com/hejiajiudeeyu/delegated-execution-dev.git
cd delegated-execution-dev/repos/client
npm install

export DELEXEC_HOME="$HOME/.delexec-responder-en"
export OPS_PORT_SUPERVISOR=8189
DELEXEC_HOME="$DELEXEC_HOME" \\
OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \\
npm run ops -- start`}
                </pre>
              </div>
            </Collapsible>

            <Note variant="warn" label="Warning">
              The following steps continue with the installed <code className="font-mono">delexec-ops</code> CLI. If you already ran the Caller quick start, either reuse the same supervisor intentionally or choose a different
              <code className="font-mono"> DELEXEC_HOME </code> and port to avoid mixing state accidentally.
            </Note>
          </Step>

          <Step
            num="02"
            title="Register a Responder identity"
            intent="Local mode does not require a hosted platform. If you do want review and catalog-style routing, bootstrap against your own or hosted platform endpoint instead."
          >
            <CodeBlock language="bash" caption="Local only">{`delexec-ops auth register --local --email me@example.com`}</CodeBlock>
            <CodeBlock language="bash" caption="Platform-backed">{`delexec-ops bootstrap \
  --email me@example.com \
  --platform http://127.0.0.1:8080`}</CodeBlock>
          </Step>

          <Step
            num="03"
            title="Enable the Responder role"
            intent="Once enabled, the runtime prepares the responder controller and the registration slots needed for Hotline drafts."
          >
            <CodeBlock language="bash">{`delexec-ops enable-responder \
  --display-name "Acme Workspace Tools"`}</CodeBlock>
          </Step>

          <Step
            num="04"
            title="Validate the built-in example Hotline first"
            intent="This keeps the first debug loop narrow. Prove the responder path works before you blame your own adapter, schema, or implementation."
          >
            <CodeBlock language="bash">{`delexec-ops add-example-hotline

BASE="http://127.0.0.1:${`$`}{OPS_PORT_SUPERVISOR:-8079}"
OPS_SESSION=$(curl -s -X POST "$BASE/auth/session/setup" \
  -H 'content-type: application/json' \
  -d '{"passphrase":"client-localtest-123"}' | jq -r '.token')
export OPS_SESSION

curl -s -X POST "$BASE/requests/example" \
  -H 'content-type: application/json' \
  -H "X-Ops-Session: $OPS_SESSION" \
  -d '{}'`}</CodeBlock>

            <Note label="Expected">
              A successful response here proves the caller, supervisor, and responder loop is already healthy.
            </Note>
          </Step>

          <Step
            num="05"
            title="Create your own Hotline registration"
            intent="A Hotline is an adapter plus contract metadata. The most important pieces are Hotline ID, summary, input_schema, output_schema, and examples."
          >
            <CodeBlock language="bash" caption="Process adapter">{`delexec-ops add-hotline \
  --type process \
  --hotline-id your.namespace.tool-name.v1 \
  --display-name "Workspace summarizer" \
  --cmd "node ./my-tool/index.js" \
  --cwd "$PWD"`}</CodeBlock>

            <CodeBlock language="bash" caption="HTTP adapter">{`delexec-ops add-hotline \
  --type http \
  --hotline-id your.namespace.tool-name.v1 \
  --display-name "Workspace summarizer" \
  --url http://127.0.0.1:9000/run`}</CodeBlock>

            <Note label="Draft location">
              The generated registration draft is written to
              <code className="font-mono"> $DELEXEC_HOME/hotline-registration-drafts/*.registration.json </code>
              so you can refine summary, schemas, examples, and risk notes before review.
              When <code className="font-mono">--cmd</code> uses a relative path, also pass
              <code className="font-mono"> --cwd </code>
              so the Responder can restart from any shell without losing the script location.
            </Note>
          </Step>

          <Step
            num="06"
            title="Optional advanced step: submit review and understand the payload"
            intent="Once the local draft works, send it to the review path only when you have a hosted or self-hosted platform endpoint. That is where schema validation, review tests, and compliance checks happen."
          >
            <CodeBlock language="bash">{`delexec-ops submit-review \
  --hotline-id your.namespace.tool-name.v1`}</CodeBlock>

            <CodeBlock language="json" caption="Typical review payload shape">{HOTLINE_ONBOARDING_BODY}</CodeBlock>
            <CodeBlock language="json" caption="One common failure shape">{SAMPLE_REVIEW_ERROR}</CodeBlock>

            <Note variant="warn" label="Important">
              No platform is still a valid setup. In that case your Hotline remains callable in local or private self-hosted environments through the supervisor catalog, without requiring any public network connection.
            </Note>
          </Step>

          <Step
            num="07"
            title="OPC #0 production rehearsal: submit to callanything.xyz"
            intent="For the public production rehearsal, use the hosted Platform endpoint and hand responder_id plus hotline_id to the operator for approval. Do not paste API keys into chat, issues, or docs."
          >
            <CodeBlock language="bash" caption="Submit review to the production Platform">{`PLATFORM="https://callanything.xyz/platform"
RESPONDER_EMAIL="me"@"example.com"

delexec-ops auth register \
  --platform "$PLATFORM" \
  --email "$RESPONDER_EMAIL"

delexec-ops enable-responder \
  --display-name "Acme Workspace Tools"

delexec-ops add-hotline \
  --type process \
  --hotline-id your.namespace.tool-name.v1 \
  --display-name "Workspace summarizer" \
  --cmd "node ./my-tool/index.js" \
  --cwd "$PWD" \
  --fixed-price-cents 50 \
  --currency PTS \
  --billing-disclosure-url "https://callanything.xyz/marketplace/responders/your-namespace"

delexec-ops submit-review \
  --hotline-id your.namespace.tool-name.v1

delexec-ops status`}</CodeBlock>

            <CodeBlock language="bash" caption="After approval: keep the Responder online on the public Relay">{`RELAY="https://callanything.xyz/relay"
export TRANSPORT_TYPE="relay_http"
export TRANSPORT_BASE_URL="$RELAY"

# Foreground: best for the first log-watching run.
delexec-ops start

# Long-running: keeps accepting tasks after an SSH or temporary shell exits.
mkdir -p "$DELEXEC_HOME/logs"
nohup env \
  TRANSPORT_TYPE="relay_http" \
  TRANSPORT_BASE_URL="$RELAY" \
  delexec-ops start \
  > "$DELEXEC_HOME/logs/public-relay-responder.log" 2>&1 &`}</CodeBlock>

            <CodeBlock language="bash" caption="Public visibility check">{`curl -s "https://callanything.xyz/marketplace/hotlines" | jq
curl -s "$PLATFORM/v1/catalog/hotlines/your.namespace.tool-name.v1" | jq`}</CodeBlock>

            <Note label="Operator handoff">
              Send the <code className="font-mono">responder_id</code>,
              <code className="font-mono"> hotline_id</code>, draft
              <code className="font-mono"> pricing_hint</code>, and contact email to the operator.
              The operator must approve, enable, and recharge the Caller; if the public <code className="font-mono">/console/</code>
              surface is not fully open yet, use the operator-provided manual channel.
              After approval, keep the local adapter online with
              <code className="font-mono"> relay_http </code>
              pointed at <code className="font-mono">https://callanything.xyz/relay</code>.
            </Note>
          </Step>

          <Step
            num="08"
            title="Check status and runtime health"
            intent="A published capability is only useful if it is actually online. Status checks are the shortest way to confirm the runtime and registration are both healthy."
          >
            <CodeBlock language="bash">{`delexec-ops status

curl -s "$BASE/responder" -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>
          </Step>
        </div>

        <div className="mt-12 border-[6px] border-black bg-[#3B82F6]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-white sm:text-3xl">What to do next</h2>
          <p className="mt-3 text-sm font-bold text-white sm:text-base">
            You now have a locally callable Hotline draft. The next decision is whether to improve the capability contract for review quality or to inspect the same system from the Caller side.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="border-4 border-black bg-white/95 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">Improve the contract</p>
              <p className="mt-2 text-sm font-bold text-black">
                Better summary, input_schema, output_schema, and examples directly improve whether Callers can understand and trust the capability.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/en/glossary/" label="Open Glossary" />
                <NextCta href="/en/faq/" label="Open FAQ" tone="white" />
              </div>
            </div>
            <div className="border-4 border-black bg-white/95 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">Switch to the buyer side</p>
              <p className="mt-2 text-sm font-bold text-black">
                A strong Responder should also understand the Caller workflow, because that is how your Hotline will actually be discovered and invoked.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/en/docs/quick-start-caller/" label="Caller Quick Start" />
                <NextCta href="/marketplace" label="Browse Marketplace" tone="white" />
              </div>
            </div>
          </div>
        </div>

        <FaqSection
          eyebrow="Responder FAQ"
          title="Responder Quick Start common questions"
          intro="These questions cover the first seller-side decisions that matter most for creating a Hotline draft, and they are also emitted as English FAQ schema."
          items={EN_ROUTE_FAQS["/en/docs/quick-start-responder/"]}
          accent="#3B82F6"
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
